import { useState } from 'react';
import { ShoppingBag, ArrowRight } from 'lucide-react';
import styles from './BestSellersByArea.module.css';
import { formatCurrency, formatNumber, formatPercentage } from '../../utils/formatters';

/**
 * BestSellersByArea Component
 * Displays top products with tabs for Value/Volume sorting
 */
function BestSellersByArea({
    title = 'Best Sellers',
    scopeLabel = 'Global',
    topByValueRows = [],
    topByVolumeRows = [],
    onNavigateSalesMix = null,
}) {
    const [activeTab, setActiveTab] = useState('value');

    const currentRows = activeTab === 'value' ? topByValueRows : topByVolumeRows;

    if (currentRows.length === 0 && topByValueRows.length === 0 && topByVolumeRows.length === 0) {
        return null;
    }

    return (
        <div className={styles.bestSellers}>
            <div className={styles.header}>
                <div className={styles.headerLeft}>
                    <div className={styles.iconWrapper}>
                        <ShoppingBag size={18} />
                    </div>
                    <div className={styles.titleGroup}>
                        <h3 className={styles.title}>{title}</h3>
                        <span className={styles.scopeLabel}>in {scopeLabel}</span>
                    </div>
                </div>

                <div className={styles.tabs}>
                    <button
                        className={`${styles.tab} ${activeTab === 'value' ? styles.active : ''}`}
                        onClick={() => setActiveTab('value')}
                    >
                        Top by Value
                    </button>
                    <button
                        className={`${styles.tab} ${activeTab === 'volume' ? styles.active : ''}`}
                        onClick={() => setActiveTab('volume')}
                    >
                        Top by Volume
                    </button>
                </div>
            </div>

            <div className={styles.content}>
                {/* Desktop Table View */}
                <div className={styles.tableWrapper}>
                    <table className={styles.table}>
                        <thead>
                            <tr>
                                <th className={styles.rankCol}>#</th>
                                <th className={styles.productCol}>Product</th>
                                <th className={styles.categoryCol}>Category</th>
                                <th className={styles.valueCol}>Revenue</th>
                                <th className={styles.volumeCol}>Volume</th>
                                <th className={styles.shareCol}>Share</th>
                            </tr>
                        </thead>
                        <tbody>
                            {currentRows.map((row, index) => (
                                <tr key={row.productId || index}>
                                    <td className={styles.rankCol}>
                                        <span className={`${styles.rank} ${index < 3 ? styles.top3 : ''}`}>
                                            {index + 1}
                                        </span>
                                    </td>
                                    <td className={styles.productCol}>
                                        <span className={styles.productName}>{row.name}</span>
                                    </td>
                                    <td className={styles.categoryCol}>
                                        <span
                                            className={styles.categoryBadge}
                                            style={{ backgroundColor: `${row.categoryColor}20`, color: row.categoryColor }}
                                        >
                                            {row.category}
                                        </span>
                                    </td>
                                    <td className={styles.valueCol}>
                                        {formatCurrency(row.revenue, { compact: true })}
                                    </td>
                                    <td className={styles.volumeCol}>
                                        {formatNumber(row.volume)}
                                    </td>
                                    <td className={styles.shareCol}>
                                        <div className={styles.shareCell}>
                                            <span>
                                                {formatPercentage(activeTab === 'value' ? row.revenueShare : row.volumeShare)}
                                            </span>
                                            <div className={styles.miniBar}>
                                                <div
                                                    className={styles.miniProgress}
                                                    style={{
                                                        width: `${activeTab === 'value' ? row.revenueShare : row.volumeShare}%`
                                                    }}
                                                />
                                            </div>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* Mobile Card View */}
                <div className={styles.cardList}>
                    {currentRows.map((row, index) => (
                        <div key={row.productId || index} className={styles.card}>
                            <div className={styles.cardHeader}>
                                <span className={`${styles.rank} ${index < 3 ? styles.top3 : ''}`}>
                                    {index + 1}
                                </span>
                                <span className={styles.productName}>{row.name}</span>
                                <span
                                    className={styles.categoryBadge}
                                    style={{ backgroundColor: `${row.categoryColor}20`, color: row.categoryColor }}
                                >
                                    {row.category}
                                </span>
                            </div>
                            <div className={styles.cardStats}>
                                <div className={styles.cardStat}>
                                    <span className={styles.cardStatLabel}>Revenue</span>
                                    <span className={styles.cardStatValue}>
                                        {formatCurrency(row.revenue, { compact: true })}
                                    </span>
                                </div>
                                <div className={styles.cardStat}>
                                    <span className={styles.cardStatLabel}>Volume</span>
                                    <span className={styles.cardStatValue}>{formatNumber(row.volume)}</span>
                                </div>
                                <div className={styles.cardStat}>
                                    <span className={styles.cardStatLabel}>Share</span>
                                    <span className={styles.cardStatValue}>
                                        {formatPercentage(activeTab === 'value' ? row.revenueShare : row.volumeShare)}
                                    </span>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {onNavigateSalesMix && (
                <div className={styles.footer}>
                    <button className={styles.salesMixButton} onClick={onNavigateSalesMix}>
                        Open Sales Mix
                        <ArrowRight size={14} />
                    </button>
                </div>
            )}
        </div>
    );
}

export default BestSellersByArea;
